import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject, timer } from 'rxjs';
import { takeUntil, map, finalize } from 'rxjs/operators';
import { OtpService, ApiResponse } from '../../services/otp.service';

export interface OtpDialogData {
  title: string;
  subtitle: string;
  type: string;
  email: string;
  id?: string;
}

@Component({
    selector: 'app-otp-dialog',
    templateUrl: './otp-dialog.component.html',
    styleUrls: ['./otp-dialog.component.scss'],
    standalone: false
})
export class OtpDialogComponent implements OnInit, OnDestroy {
  otpForm: UntypedFormGroup;
  isVerifying = false;
  isResending = false;
  isSending = false; 
  errorMessage = '';
  resendDisabled = true;
  resendCountdown = 60; 
  maskedEmail = '';
  otpSent = false; 
  
  private destroy$ = new Subject<void>();
  private timerSubscription?: any;

  constructor(
    private fb: UntypedFormBuilder,
    private otpService: OtpService,
    public dialogRef: MatDialogRef<OtpDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OtpDialogData
  ) {
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
    
    this.maskedEmail = this.maskEmail(data.email);
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  sendOtp() {
    this.isSending = true;
    this.errorMessage = '';

    this.otpService.sendOtp({
      type: this.data.type,
      email: this.data.email,
      id: this.data.id
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isSending = false;
      })
    ).subscribe({
      next: (response: ApiResponse) => {
        this.otpSent = true;
        this.startResendTimer();
      },
      error: (error) => {
        this.errorMessage = 'Failed to send OTP. Please try again.';
      }
    });
  }

  private startResendTimer() {
    if (this.timerSubscription) this.timerSubscription.unsubscribe();

    this.resendDisabled = true;
    this.resendCountdown = 60;
    
    this.timerSubscription = timer(0, 1000).pipe(
      takeUntil(this.destroy$),
      map((i: number) => 60 - i)
    ).subscribe({
      next: (countdown: number) => {
        this.resendCountdown = countdown;
        if (countdown <= 0) {
          this.resendDisabled = false;
          this.resendCountdown = 0;
          this.timerSubscription?.unsubscribe();
        }
      }
    });
  }

  private maskEmail(email: string): string {
    if (email.length <= 4) {
      return email;
    }
    
    const prefix = email.substring(0, 3);
    const suffix = email.substring(email.length - 4);
    const masked = '***'.repeat(Math.max(1, Math.floor((email.length - 7) / 3)));
    
    return `${prefix} ${masked} ${suffix}`;
  }

  getErrorMessage(field: string): string {
    const control = this.otpForm.get(field);
    if (control?.hasError('required')) {
      return `${field} is required`;
    }
    if (control?.hasError('pattern')) {
      return 'OTP must be 6 digits';
    }
    return '';
  }

  onOtpInput(event: any) {
    const value = event.target.value.replace(/\D/g, '');
    this.otpForm.patchValue({ otp: value });
    this.errorMessage = '';
  }

  verifyOtp() {
    if (this.otpForm.invalid) {
      return;
    }

    this.isVerifying = true;
    this.errorMessage = '';

    const otpValue = this.otpForm.value.otp;

    this.otpService.verifyOtp({
      email: this.data.email,
      otp: otpValue,
      type: this.data.type,
      id: this.data.id
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isVerifying = false;
      })
    ).subscribe({
      next: (response: ApiResponse) => {
        if (response.success) {
          this.dialogRef.close(true);
        } else {
          this.errorMessage = response.message || 'Invalid OTP. Please try again.';
          this.otpForm.patchValue({ otp: '' });
        }
      },
      error: (error) => {
        this.errorMessage = 'Verification failed. Please try again.';
        this.otpForm.patchValue({ otp: '' });
      }
    });
  }

  resendOtp() {
    if (this.resendDisabled) {
      return;
    }

    this.isResending = true;
    this.errorMessage = '';

    this.otpService.sendOtp({
      type: this.data.type,
      email: this.data.email,
      id: this.data.id
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isResending = false;
      })
    ).subscribe({
      next: (response: ApiResponse) => {
        this.startResendTimer();
        this.otpForm.patchValue({ otp: '' });
      },
      error: (error) => {
        this.errorMessage = 'Failed to resend OTP. Please try again.';
      }
    });
  }

  getSendButtonText(): string {
    if (this.isSending) return 'Sending...';
    return 'Send OTP';
  }

  getResendButtonText(): string {
    if (this.isResending) return 'Sending...';
    if (this.resendDisabled && this.resendCountdown > 0) {
      return `Resend OTP (${this.resendCountdown}s)`;
    }
    return 'Resend OTP';
  }

  close() {
    this.dialogRef.close(false);
  }
}